import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SidebarIcon } from '../components/SidebarIcon';
import './PatientDashboardPage.css';
import '../components/DirectionsLink.css';
import { useDirectionsNavigation } from '../components/useDirectionsNavigation';
import { API_BASE_URL } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';

// TODO: really FullAppointment:
interface Appointment {
  id: string;
  when: string;
  duration: number;
  userIds: string[];
  procedureIds: string[];
  practitionerIds?: string[];
  procedureName?: string;
  procedureType?: string;
  practitionerName?: string;
  // existing but not used:
  // patientIds: string[];
  // practitionerIds: string[];
  // meetingId: string
  // type?: string;
  // [meetingInfo: string]: any;
  // [appointmentInfo: string]: any;

}

/*
        fullAppointment.put("id", appointment.getId());
        fullAppointment.put("meetingId", appointment.getMeetingId());
        fullAppointment.put("appointmentInfo", appointment.getInfo());
        fullAppointment.put("when", meeting.getWhen());
        fullAppointment.put("duration", meeting.getDuration());
        fullAppointment.put("type", meeting.getType());
        fullAppointment.put("userIds", userIds);
        fullAppointment.put("procedureIds",  appointment.getProcedureIds());
        fullAppointment.put("practitionerIds", practitionerIds);
        fullAppointment.put("patientIds", patientIds);
        fullAppointment.put("meetingInfo", meeting.getInfo());
        */

interface Procedure {
  id: string;
  name: string;
  type: string;
  duration: number;
  [ruleMap: string]: any;
}

interface Bill {
  date?: string;
  due?: string;
  payDate?: string;
  amount?: number;
  id?: string;
  [key: string]: any;
}

interface Docu {
  id?: string;
  title?: string;
  date?: string;
  [key: string]: any;
}

// /api/user/titled-name/{userId}

const translateProcedureName = (procedureName?: string) => {
  if (!procedureName) return 'Behandlung';
  const translatedName = t(procedureName as TranslationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const readStoredPatientId = () => {
  const raw = localStorage.getItem('loginPatientId');
  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object') {
      const candidate = (parsed as { patientId?: unknown; id?: unknown }).patientId ?? (parsed as { id?: unknown }).id;
      return typeof candidate === 'string' ? candidate : '';
    }
    return '';
  } catch {
    return raw.trim();
  }
};

export function PatientDashboardPage() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [, setBills] = useState<Bill[]>([]);
  const [docuList, setDocuList] = useState<Docu[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [loadingBills, setLoadingBills] = useState(true);
  const [loadingDocus, setLoadingDocus] = useState(true);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('Patient');
  const navigate = useNavigate();
  const openDirections = useDirectionsNavigation();

  const procedureCacheKey = 'procedureCache';
  const practitionerCacheKey = 'practitionerTitledNameCache';

  const readProcedureCache = () => {
    try {
      const raw = localStorage.getItem(procedureCacheKey);
      if (!raw) return {} as Record<string, Procedure>;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : ({} as Record<string, Procedure>);
    } catch (cacheError) {
      console.error('Failed to read procedure cache', cacheError);
      return {} as Record<string, Procedure>;
    }
  };

  const writeProcedureCache = (cache: Record<string, Procedure>) => {
    try {
      localStorage.setItem(procedureCacheKey, JSON.stringify(cache));
    } catch (cacheError) {
      console.error('Failed to write procedure cache', cacheError);
    }
  };

  const readPractitionerCache = () => {
    try {
      const raw = localStorage.getItem(practitionerCacheKey);
      if (!raw) return {} as Record<string, string>;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : ({} as Record<string, string>);
    } catch (cacheError) {
      console.error('Failed to read practitioner cache', cacheError);
      return {} as Record<string, string>;
    }
  };

  const writePractitionerCache = (cache: Record<string, string>) => {
    try {
      localStorage.setItem(practitionerCacheKey, JSON.stringify(cache));
    } catch (cacheError) {
      console.error('Failed to write practitioner cache', cacheError);
    }
  };

  const bookAppointment = () => {
    navigate('/procedures');
  };

  const cancelAppointment = (appointment: Appointment) => {
    const cancellationAppointment = {
      id: appointment.id,
      when: appointment.when,
      procedureName: translateProcedureName(appointment.procedureName),
      practitionerIds: appointment.practitionerIds,
      previousPage: '/dashboard',
    };

    localStorage.setItem('selectedAppointmentId', appointment.id);
    localStorage.setItem('previousPage', '/dashboard');
    localStorage.setItem('pendingCancellationAppointment', JSON.stringify(cancellationAppointment));
    navigate('/cancellation', { state: { appointment: cancellationAppointment } });
  };

  const moveAppointment = async (appointmentId: string) => {
    if (!appointmentId) {
      console.error('appointmentId is undefined');
      return;
    }
    localStorage.setItem('selectedAppointmentId', appointmentId);
    localStorage.setItem('previousPage', '/dashboard');

    localStorage.setItem('selectedPractitionerId', '');
    localStorage.setItem('selectedProcedureId', '');
    localStorage.removeItem('pendingAppointmentFull');
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const apiCallFull = API_BASE_URL + '/appointments/get-full-appointment/' + appointmentId;
        const response = await fetch(apiCallFull, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const fullAppointment = await response.json();
          const practitionerIds = Array.isArray(fullAppointment?.practitionerIds)
            ? fullAppointment.practitionerIds
            : Array.isArray(fullAppointment?.practitionersIds)
              ? fullAppointment.practitionersIds
              : [];
          const procedureIds = Array.isArray(fullAppointment?.procedureIds)
            ? fullAppointment.procedureIds
            : [];
          const selectedPractitionerId = practitionerIds[0];
          const selectedProcedureId = procedureIds[0];

          if (selectedPractitionerId) {
            localStorage.setItem('selectedPractitionerId', selectedPractitionerId);
          }

          if (selectedProcedureId) {
            localStorage.setItem('selectedProcedureId', selectedProcedureId);
          }

          localStorage.setItem(
            'pendingAppointmentFull',
            JSON.stringify({ ...fullAppointment, practitionerIds, procedureIds })
          );
        } else {
          console.error('Bad full appointment response for edit=' + response.status);
        }
      } catch (fetchError) {
        console.error('Failed to load full appointment in handleEdit', fetchError);
      }
    }

    console.log(
      'handleEdit: appointmentId=' + appointmentId + ' practitionerId=' + localStorage.getItem('selectedPractitionerId')
    );

    navigate('/date-time-entry');
  };
  const downloadDocu = (_index: number) => {};


  useEffect(() => {
    const storedName = localStorage.getItem('userFullName') || localStorage.getItem('loginUserName');
    if (storedName) setUserName(storedName);

    const onStorage = (event: StorageEvent) => {
      if (event.key === 'userFullName' || event.key === 'loginUserName') {
        setUserName(event.newValue || 'Patient');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const fetchAppointments = async () => {
      const token = localStorage.getItem('token');
      const loginUserId = localStorage.getItem('loginUserId');
      if (!token || !loginUserId) {
        setError('Not authenticated');
        setLoadingAppointments(false);
        return;
      }

      console.log("/appointments/search userId=" + loginUserId);

      try {
        const apiCall = API_BASE_URL + '/appointments/search';
        const searchRes = await fetch(apiCall, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: loginUserId }),
        });

        if (!searchRes.ok) throw new Error('Search failed');

        const appointmentIds = await searchRes.json();

        //console.log("  appointmentIds=" + JSON.stringify(appointmentIds));

        const appointmentPromises = appointmentIds.map(async (id: string) => {
          const apiCallFull = API_BASE_URL + '/appointments/get-full-appointment/' + id;
          const res = await fetch(apiCallFull, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!res.ok) throw new Error(`Fehler ${id}`);

          //console.log("appointmentId=" + id + ": " + JSON.stringify(res));

          return res.json();
        });

        const appointmentDetails = await Promise.all(appointmentPromises);

        //console.log("appointmentDetails=" + JSON.stringify(appointmentDetails));

        const procedureCache = readProcedureCache();
        const practitionerCache = readPractitionerCache();

        const resolveProcedure = async (procedureId: string) => {
          const cachedProcedure = procedureCache[procedureId];
          if (cachedProcedure?.name && cachedProcedure?.type) {

            //console.log("cachedProcedure=" + JSON.stringify(cachedProcedure));

            return cachedProcedure;
          }

          try {
            const response = await fetch(`${API_BASE_URL}/procedures/${procedureId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              console.log('Bad procedure response=' + JSON.stringify(response));
              return undefined;
            }

            const procedure = (await response.json()) as Procedure;
            if (procedure?.id && procedure?.name) {
              procedureCache[procedureId] = procedure;
              writeProcedureCache(procedureCache);

              console.log("procedure=" + JSON.stringify(procedure));

              return procedure;
            }
          } catch (detailError) {
            console.error('Failed to load procedure', detailError);
          }

          return undefined;
        };

        const resolvePractitionerName = async (practitionerId?: string) => {
          if (!practitionerId) {
            return undefined;
          }

          const cachedName = practitionerCache[practitionerId];
          if (cachedName) {
            return cachedName;
          }

          try {
            const response = await fetch(`${API_BASE_URL}/practitioners/titled-name/${practitionerId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              console.log(`Bad practitioner titled-name response for ${practitionerId}: ${response.status}`);
              return undefined;
            }

            const contentType = response.headers.get('content-type') || '';
            const payload = contentType.includes('application/json')
              ? await response.json()
              : await response.text();

            const practitionerName =
              typeof payload === 'string'
                ? payload.trim()
                : typeof payload?.titledName === 'string'
                  ? payload.titledName.trim()
                  : typeof payload?.name === 'string'
                    ? payload.name.trim()
                    : typeof payload?.fullName === 'string'
                      ? payload.fullName.trim()
                      : '';

            if (!practitionerName) {
              return undefined;
            }

            practitionerCache[practitionerId] = practitionerName;
            writePractitionerCache(practitionerCache);
            return practitionerName;
          } catch (detailError) {
            console.error('Failed to load practitioner titled name', detailError);
            return undefined;
          }
        };

        const appointmentWithProcedureNames = await Promise.all(
          appointmentDetails.map(async (appointment) => {
            const procedureId = Array.isArray(appointment.procedureIds) ? appointment.procedureIds[0] : undefined;
            const practitionerId = Array.isArray(appointment.practitionerIds) ? appointment.practitionerIds[0] : undefined;

            const [procedure, practitionerName] = await Promise.all([
              procedureId ? resolveProcedure(procedureId) : Promise.resolve(undefined),
              resolvePractitionerName(practitionerId),
            ]);

            return {
              ...appointment,
              procedureName: procedure?.name ?? appointment.procedureName,
              procedureType: procedure?.type ?? appointment.procedureType,
              practitionerName,
            };
          })
        );

        setAppointments(appointmentWithProcedureNames);
      } catch (err) {
        setError(`Fehler: ${err}`);
      } finally {
        setLoadingAppointments(false);
      }
    };

    fetchAppointments();
  }, []);

  useEffect(() => {
    const fetchBills = async () => {
      const token = localStorage.getItem('token');
      const patientId = readStoredPatientId();

      if (!token) {
        setError('Not authenticated');
        setLoadingBills(false);
        return;
      }

      if (!patientId) {
        setBills([]);
        setLoadingBills(false);
        return;
      }

      console.log('patientId=' + patientId);

      try {
        const response = await fetch(`${API_BASE_URL}/bills/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ patientId }),
        });

        if (!response.ok) {
          console.error('Search for bills failed: ' + response.status + ' ' + response.statusText);
          throw new Error('Search for bills failed: ' + response.status + ' ' + response.statusText);
        }

        const billIds = await response.json();
        if (!Array.isArray(billIds)) {
          setBills([]);
          return;
        }

        const billPromises = billIds.map(async (billId: string) => {
          const billResponse = await fetch(`${API_BASE_URL}/bills/id/${billId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!billResponse.ok) {
            throw new Error(`Error ${billId}`);
          }
          return billResponse.json();
        });

        const billDetails = await Promise.all(billPromises);
        setBills(billDetails);
      } catch (err) {
        setError(`Fehler: ${err}`);
      } finally {
        setLoadingBills(false);
      }
    };

    fetchBills();
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      const token = localStorage.getItem('token');
      const patientId = readStoredPatientId();

      if (!token) {
        setError('Not authenticated');
        setLoadingDocus(false);
        return;
      }

      if (!patientId) {
        setDocuList([]);
        setLoadingDocus(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/documents/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ patientId }),
        });

        if (!response.ok) {
          console.error('Search for documents failed: ' + response.status + ' ' + response.statusText);
          throw new Error('Search for documents failed: ' + response.status + ' ' + response.statusText);
        }

        const docuIds = await response.json();
        if (!Array.isArray(docuIds)) {
          setDocuList([]);
          return;
        }

        const docuPromises = docuIds.map(async (docuId: string) => {
          const docuResponse = await fetch(`${API_BASE_URL}/documents/id/${docuId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!docuResponse.ok) {
            throw new Error(`Error ${docuId}`);
          }
          return docuResponse.json();
        });

        const docuDetails = await Promise.all(docuPromises);
        setDocuList(docuDetails);
      } catch (err) {
        setError(`Error: ${err}`);
      } finally {
        setLoadingDocus(false);
      }
    };

    fetchDocuments();
  }, []);

  const recentDocus = useMemo(() => docuList.slice(0, 3), [docuList]);
  const now = useMemo(() => new Date(), []);
  const futureAppointments = useMemo(() => {
    return appointments
      .filter((appointment) => new Date(appointment.when) >= now)
      .sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime());
  }, [appointments, now]);
  const nextAppointment = futureAppointments[0];
  const isNextAppointmentProcedureSeries = nextAppointment?.procedureType === 'series';
  const nextAppointmentSeriesTooltip = isNextAppointmentProcedureSeries ? t('no-chain-cancellation') : undefined;
  const nextAppointmentSeriesTooltipId = nextAppointment
    ? `no-chain-cancellation-${nextAppointment.id}`
    : undefined;

  if (loadingAppointments || loadingBills || loadingDocus) {
    return <div className="text-center p-4">Übersicht wird geladen...</div>;
  }

  if (error) return <div className="text-red-600 p-4">{error}</div>;

  //setError(`${t('registration_failed')}: ${err instanceof Error ? err.message : t('network_error')}`);
  // {nextAppointment.procedureName
  // `${t(nextAppointment.procedureName)}`

  return (
    <div className="patient-dashboard">
      <main className="patient-dashboard__content">
        <header className="patient-dashboard__greeting">
          <h1>Guten Tag, <strong>{userName}</strong></h1>
          <p>Willkommen zurück in Ihrem persönlichen Patientenbereich.</p>
        </header>

        <button type="button" onClick={bookAppointment} className="patient-dashboard__booking">
          <span className="patient-dashboard__plus" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" focusable="false">
              <path d="M5 12h14M12 5v14" stroke="currentColor" strokeWidth="2.5" />
            </svg>
          </span>
          <span className="patient-dashboard__booking-copy">
            <span className="patient-dashboard__booking-title">Neuen Termin buchen</span>
            <span className="patient-dashboard__booking-description">Wählen Sie Ihren Wunschtermin online</span>
          </span>
        </button>

        <section className="patient-dashboard__appointment">
          <Link
            to="/appointments"
            className="patient-dashboard__appointment-link"
            aria-label="Meine Termine anzeigen"
            tabIndex={0}
          />
          <h2>Nächster Termin</h2>
          {nextAppointment ? (
            <div className="patient-dashboard__appointment-row">
              <div className="patient-dashboard__date">
                <span>{new Date(nextAppointment.when).toLocaleDateString('de-DE', { month: 'short' }).toUpperCase()}</span>
                <strong>{new Date(nextAppointment.when).getDate().toString().padStart(2, '0')}</strong>
              </div>
              <div className="patient-dashboard__appointment-copy">
                <p className="patient-dashboard__time">
                  <SidebarIcon name="clock" />
                  <span>{new Date(nextAppointment.when).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr{nextAppointment.duration ? ` · ${nextAppointment.duration} Min.` : ''}</span>
                </p>
                <h3 lang="de">{translateProcedureName(nextAppointment.procedureName)}</h3>
                {nextAppointment.practitionerName ? (
                  <p className="patient-dashboard__practitioner">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <circle cx="12" cy="7" r="4" />
                      <path d="M4 21v-2a8 5 0 0 1 16 0v2H4Z" />
                    </svg>
                    <span>{nextAppointment.practitionerName}</span>
                  </p>
                ) : null}
              </div>
              <div className="patient-dashboard__appointment-actions">
                <div className="patient-dashboard__action-buttons">
                  <button type="button" onClick={() => moveAppointment(nextAppointment.id)} className="patient-dashboard__move">
                    Verschieben
                  </button>
                  <span className="group relative inline-flex" tabIndex={isNextAppointmentProcedureSeries ? 0 : undefined}>
                    <button
                      type="button"
                      onClick={() => cancelAppointment(nextAppointment)}
                      disabled={isNextAppointmentProcedureSeries}
                      aria-describedby={isNextAppointmentProcedureSeries ? nextAppointmentSeriesTooltipId : undefined}
                      className="patient-dashboard__cancel"
                    >
                      Absagen
                    </button>
                    {nextAppointmentSeriesTooltip ? (
                      <span
                        id={nextAppointmentSeriesTooltipId}
                        role="tooltip"
                        className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 w-80 rounded-lg bg-slate-800 px-3 py-2 text-left text-xs font-medium leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus:opacity-100"
                      >
                        {nextAppointmentSeriesTooltip}
                      </span>
                    ) : null}
                  </span>
                </div>
                <button type="button" onClick={() => openDirections(nextAppointment.id)} className="patient-dashboard__directions portal-directions-link">
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 1.8a5.3 5.3 0 0 0-5.3 5.3c0 4 5.3 10.7 5.3 10.7s5.3-6.7 5.3-10.7A5.3 5.3 0 0 0 10 1.8Zm0 3.5a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6Z" clipRule="evenodd" />
                  </svg>
                  <span>Anfahrt und Parken</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="patient-dashboard__empty">Keine anstehenden Termine.</div>
          )}
        </section>

        <div className="patient-dashboard__lower-grid">
          <section className="patient-dashboard__advice">
            <h2>Ratgeber und Tipps</h2>
            <Link to="/instructions" className="patient-dashboard__advice-card">
              <span className="patient-dashboard__bulb" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2a7 7 0 0 0-4.5 12.4c.8.7 1 1.4 1 2.1V18h7v-1.5c0-.7.2-1.4 1-2.1A7 7 0 0 0 12 2Zm-3 18h6v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-1Z" />
                </svg>
              </span>
              <div className="patient-dashboard__advice-copy">
                <p className="patient-dashboard__category">Chirurgische Eingriffe</p>
                <h3>Tipps bei chirurgischen Eingriffen</h3>
                <p className="patient-dashboard__advice-description">Präoperative und postoperative Verhaltensregeln.</p>
              </div>
              <span className="patient-dashboard__arrow" aria-hidden="true">→</span>
            </Link>
          </section>
          <section className="patient-dashboard__documents">
            <div className="patient-dashboard__section-heading">
              <h2>Dokumente</h2>
              <Link to="/documents" className="patient-dashboard__all" aria-label="Alle Dokumente ansehen">Alle</Link>
            </div>
            {recentDocus.length === 0 ? (
              <Link to="/documents" className="patient-dashboard__advice-card patient-dashboard__practice-document">
                <span className="patient-dashboard__bulb" aria-hidden="true">
                  <SidebarIcon name="document" />
                </span>
                <span className="patient-dashboard__advice-copy patient-dashboard__practice-document-copy">
                  Patienteninformation zur Eigenbluttherapie mit körpereigenen Wachstumsfaktoren in der Zahnmedizin.
                </span>
                <span className="patient-dashboard__arrow" aria-hidden="true">→</span>
              </Link>
            ) : recentDocus.map((docu, index) => (
              <div className="patient-dashboard__document" key={`${docu.id ?? 'docu'}-${index}`}>
                <span className="patient-dashboard__attachment" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M9 15h7a4 4 0 0 0 0-8H8a5 5 0 0 0 0 10h7M9 10h7a1 1 0 0 1 0 2H9" />
                  </svg>
                </span>
                <div>
                  <p className="patient-dashboard__document-title">{docu.title || 'untitled'}</p>
                  <p className="patient-dashboard__document-date">{docu.date ? (Number.isNaN(new Date(docu.date).getTime()) ? docu.date : new Date(docu.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })) : '--.--.----'}</p>
                </div>
                <button type="button" onClick={() => downloadDocu(index)} className="patient-dashboard__download" aria-label={`${docu.title || 'Dokument'} herunterladen`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M9 3h6v7h4l-7 8-7-8h4V3ZM4 20h16v2H4v-2Z" />
                  </svg>
                </button>
              </div>
            ))}
          </section>
        </div>
      </main>
    </div>
  );
}
